import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ff018244-580d-47eb-b754-fa20aec11c8d/artifacts/2zc3j2vv_jini%20studio%20logo%20.png";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-neutral-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Jini Studio" className="h-16 w-auto" />
          </Link>
          <Link to="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="font-display text-4xl font-bold text-neutral-900 mb-8">Privacy Policy</h1>
        <p className="text-sm text-neutral-500 mb-8">Last updated: February 1, 2026</p>

        <div className="prose prose-neutral max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">1. Introduction</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              Zaroori Retail ("we", "us", or "our") operates the Jini Studio AI platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-semibold text-neutral-800 mb-3">Personal Information</h3>
            <p className="text-neutral-600 leading-relaxed mb-4">When you register for an account, we collect:</p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4 mb-4">
              <li>Name and email address</li>
              <li>Company name and GST number (for business accounts)</li>
              <li>Payment information (processed securely via Razorpay)</li>
              <li>Profile picture (if provided via Google OAuth)</li>
            </ul>

            <h3 className="text-lg font-semibold text-neutral-800 mb-3">Content Data</h3>
            <p className="text-neutral-600 leading-relaxed mb-4">When you use our Service, we process:</p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4 mb-4">
              <li>Product images you upload for AI photoshoots</li>
              <li>AI-generated images and videos</li>
              <li>Metadata associated with your shoots (model selections, backgrounds, etc.)</li>
            </ul>

            <h3 className="text-lg font-semibold text-neutral-800 mb-3">Usage Data</h3>
            <p className="text-neutral-600 leading-relaxed mb-4">We automatically collect:</p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4">
              <li>IP address and device information</li>
              <li>Browser type and version</li>
              <li>Pages visited and features used</li>
              <li>Time and date of visits</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">We use your information to:</p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4">
              <li>Provide, operate, and maintain our Service</li>
              <li>Process your AI photoshoot requests</li>
              <li>Process payments and manage subscriptions</li>
              <li>Send you service-related communications</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Improve our Service and develop new features</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">4. Data Storage and Security</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              Your data is stored on secure cloud servers. We implement industry-standard security measures including:
            </p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4">
              <li>Encryption of data in transit (SSL/TLS) and at rest</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Regular backups and disaster recovery procedures</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">5. Data Retention</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              We retain your personal information for as long as your account is active. Uploaded images are retained for 90 days after processing. Generated assets are retained according to your subscription plan:
            </p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4">
              <li><strong>Starter Plan:</strong> 30 days</li>
              <li><strong>Pro Plan:</strong> 90 days</li>
              <li><strong>Studio Plan:</strong> Unlimited (while subscription is active)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">6. Third-Party Services</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">We use the following third-party services:</p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4">
              <li><strong>Google OAuth:</strong> For secure authentication</li>
              <li><strong>Razorpay:</strong> For payment processing</li>
              <li><strong>AI Service Providers:</strong> For image and video generation (data is processed and not retained)</li>
              <li><strong>Cloud Storage Providers:</strong> For secure asset storage</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">7. AI Processing & Virtual Model Disclosure</h2>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <p className="text-purple-800 font-medium mb-2">AI-Generated Content Notice</p>
              <p className="text-purple-700 text-sm">
                All human-like models displayed in our generated content are 100% AI-generated virtual representations. 
                They are not based on or represent any real individuals.
              </p>
            </div>
            <p className="text-neutral-600 leading-relaxed mb-4">
              <strong>How AI Processing Works:</strong> When you upload product images, our AI analyzes the product characteristics 
              and generates photorealistic images with AI-created virtual models. Your original images are used only as reference 
              for product appearance and are not stored on third-party AI servers.
            </p>
            <p className="text-neutral-600 leading-relaxed mb-4">
              <strong>Virtual Model Rights:</strong> The AI-generated human models in your content:
            </p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4 mb-4">
              <li>Are entirely computer-generated and synthetic</li>
              <li>Do not depict any real person, living or deceased</li>
              <li>Require no model release or consent forms</li>
              <li>Are fully licensed for your commercial use per your subscription</li>
            </ul>
            <p className="text-neutral-600 leading-relaxed mb-4">
              <strong>AI Training:</strong> Your uploaded images are NOT used to train AI models. They are processed only to 
              generate your requested outputs and then handled according to our data retention policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">8. Your Rights</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">You have the right to:</p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4">
              <li>Access your personal data</li>
              <li>Correct inaccurate personal data</li>
              <li>Request deletion of your personal data</li>
              <li>Object to processing of your personal data</li>
              <li>Export your data in a portable format</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p className="text-neutral-600 leading-relaxed mt-4">
              To exercise these rights, please contact us at privacy@jinistudio.ai.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">9. Cookies</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              We use essential cookies for authentication and session management. We do not use tracking or advertising cookies. By using our Service, you consent to our use of essential cookies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">10. Children's Privacy</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal data, please contact us.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">11. Changes to This Policy</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">12. Contact Us</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              If you have questions about this Privacy Policy, please contact us:
            </p>
            <div className="bg-neutral-50 rounded-lg p-4 text-neutral-600">
              <p className="font-medium">Zaroori Retail (Jini Studio AI)</p>
              <p>Email: privacy@jinistudio.ai</p>
              <p>Address: Mumbai, Maharashtra, India</p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-neutral-100 py-8 px-6 mt-12">
        <div className="max-w-4xl mx-auto text-center text-neutral-600 text-sm">
          <p>© 2025 Jini Studio by Zaroori Retail. All rights reserved.</p>
          <div className="flex items-center justify-center gap-4 mt-4">
            <Link to="/terms" className="hover:text-indigo-600">Terms & Conditions</Link>
            <span>•</span>
            <Link to="/refund" className="hover:text-indigo-600">Refund Policy</Link>
            <span>•</span>
            <Link to="/contact" className="hover:text-indigo-600">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
